import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import type { GridPaginationModel } from '@mui/x-data-grid';
import type { ContextMenuItem, ContextMenuParams } from '@core/components';
import { useCrudListActions } from './useCrudListActions';
import { type ListFilterConfig, useListFilter } from './useListFilter';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

export interface CrudListPageQuery {
  offset: number;
  limit: number;
}

export interface CrudListPageResult<T> {
  items: T[];
  total: number;
}

export interface UseCrudListPageOptions<
  T,
  F,
  Q extends object = Record<string, unknown>,
> {
  loadPage: (query: Q & CrudListPageQuery) => Promise<CrudListPageResult<T>>;
  toQueryParams?: (searchTerm: string, filters: F) => Q;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  deleteItem?: (item: T) => Promise<void>;
  loadErrorMessage: string;
  deleteSuccessMessage?: string;
  deleteErrorMessage?: string;
  refreshContext?: () => Promise<void> | void;
  filter?: ListFilterConfig<F>;
  disabled?: boolean;
}

type UseCrudListPageSearchReturn = {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
};

type UseCrudListPageFilterReturn<F> = {
  filters: F;
  setFilters: Dispatch<SetStateAction<F>>;
  clearFilters: () => void;
  filterOpen: boolean;
  openFilters: () => void;
  closeFilters: () => void;
  activeFilterCount: number;
};

type UseCrudListPageBaseReturn<T> = UseCrudListPageSearchReturn & {
  items: T[];
  setItems: Dispatch<SetStateAction<T[]>>;
  filteredItems: T[];
  loading: boolean;
  dialogOpen: boolean;
  editingItem: T | null;
  load: () => Promise<void>;
  openCreate: () => void;
  openEdit: (item: T) => void;
  closeDialog: () => void;
  deleteConfirm: T | null;
  requestDelete: (item: T) => void;
  cancelDelete: () => void;
  confirmDelete: () => Promise<void>;
  deleteLoading: boolean;
  getRowId: (item: T) => string;
  findByRowId: (rowId: string) => T | undefined;
  contextMenuItems: (params: ContextMenuParams) => ContextMenuItem[];
  paginationMode: 'server';
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  pageSizeOptions: number[];
};

export type UseCrudListPageReturn<T, F> = UseCrudListPageBaseReturn<T> &
  (F extends undefined ? object : UseCrudListPageFilterReturn<F>);

export function useCrudListPage<
  T extends { id: number },
  F = undefined,
  Q extends object = Record<string, unknown>,
>(options: UseCrudListPageOptions<T, F, Q>): UseCrudListPageReturn<T, F> {
  const {
    loadPage,
    toQueryParams,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    deleteItem,
    loadErrorMessage,
    deleteSuccessMessage,
    deleteErrorMessage,
    refreshContext,
    filter,
    disabled = false,
  } = options;

  const [items, setItems] = useState<T[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: defaultPageSize,
  });
  const requestSeq = useRef(0);

  const {
    filters,
    setFilters,
    clearFilters,
    filterOpen,
    openFilters,
    closeFilters,
    activeFilterCount,
    getQuery,
  } = useListFilter({
    filter,
    searchTerm,
    toQueryParams,
  });

  const listQuery = getQuery();
  // Search and filters start again from the first page.
  const [pageQuery, setPageQuery] = useState(listQuery);
  if (listQuery !== pageQuery) {
    setPageQuery(listQuery);
    setPaginationModel((model) =>
      model.page === 0 ? model : { ...model, page: 0 }
    );
  }

  const loadPageItems = useCallback(async () => {
    const requestId = ++requestSeq.current;
    const limit = paginationModel.pageSize;
    const offset = paginationModel.page * limit;
    setLoading(true);
    try {
      const page = await loadPage({
        ...(listQuery ?? ({} as Q)),
        offset,
        limit,
      });
      if (requestId !== requestSeq.current) {
        return false;
      }

      const lastPage = page.total === 0 ? 0 : Math.ceil(page.total / limit) - 1;
      if (paginationModel.page > lastPage) {
        setPaginationModel((model) => ({ ...model, page: lastPage }));
        return true;
      }

      setItems(page.items);
      setRowCount(page.total);
      return true;
    } catch {
      if (requestId === requestSeq.current) {
        toast.error(loadErrorMessage);
      }
      return false;
    } finally {
      if (requestId === requestSeq.current) {
        setLoading(false);
      }
    }
  }, [
    loadPage,
    listQuery,
    paginationModel.page,
    paginationModel.pageSize,
    loadErrorMessage,
  ]);

  const load = useCallback(async () => {
    const loaded = await loadPageItems();
    if (loaded && refreshContext) {
      await refreshContext();
    }
  }, [loadPageItems, refreshContext]);

  useEffect(() => {
    if (disabled) {
      setItems([]);
      setRowCount(0);
      setLoading(false);
      return;
    }

    void loadPageItems();
  }, [disabled, loadPageItems]);

  const onPaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((current) =>
      current.page === model.page && current.pageSize === model.pageSize
        ? current
        : model
    );
  }, []);

  const {
    dialogOpen,
    editingItem,
    openCreate,
    openEdit,
    closeDialog,
    deleteConfirm,
    requestDelete,
    cancelDelete,
    confirmDelete,
    deleteLoading,
    getRowId,
    findByRowId,
    contextMenuItems,
  } = useCrudListActions({
    items,
    load,
    deleteItem,
    deleteSuccessMessage,
    deleteErrorMessage,
  });

  return {
    items,
    setItems,
    filteredItems: items,
    loading,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    clearFilters,
    filterOpen,
    openFilters,
    closeFilters,
    activeFilterCount,
    dialogOpen,
    editingItem,
    load,
    openCreate,
    openEdit,
    closeDialog,
    deleteConfirm,
    requestDelete,
    cancelDelete,
    confirmDelete,
    deleteLoading,
    getRowId,
    findByRowId,
    contextMenuItems,
    paginationMode: 'server',
    rowCount,
    paginationModel,
    onPaginationModelChange,
    pageSizeOptions,
  } as unknown as UseCrudListPageReturn<T, F>;
}
